/**
 * spanishInflections.js
 * Comprehensive Spanish verb conjugations, noun pluralizations,
 * and adjective agreement forms with support for structured AI data
 * and smart linguistic fallbacks.
 */

// Common irregular verbs present & preterite mappings
const COMMON_IRREGULARS = {
  ser: {
    present: ["soy", "eres", "es", "somos", "sois", "son"],
    preterite: ["fui", "fuiste", "fue", "fuimos", "fuisteis", "fueron"],
    imperfect: ["era", "eras", "era", "éramos", "erais", "eran"],
    future: ["seré", "serás", "será", "seremos", "seréis", "serán"],
    gerund: "siendo",
    participle: "sido",
  },
  estar: {
    present: ["estoy", "estás", "está", "estamos", "estáis", "están"],
    preterite: ["estuve", "estuviste", "estuvo", "estuvimos", "estuvisteis", "estuvieron"],
    imperfect: ["estaba", "estabas", "estaba", "estábamos", "estabais", "estaban"],
    future: ["estaré", "estarás", "estará", "estaremos", "estaréis", "estarán"],
    gerund: "estando",
    participle: "estado",
  },
  ir: {
    present: ["voy", "vas", "va", "vamos", "vais", "van"],
    preterite: ["fui", "fuiste", "fue", "fuimos", "fuisteis", "fueron"],
    imperfect: ["iba", "ibas", "iba", "íbamos", "ibais", "iban"],
    future: ["iré", "irás", "irá", "iremos", "iréis", "irán"],
    gerund: "yendo",
    participle: "ido",
  },
  tener: {
    present: ["tengo", "tienes", "tiene", "tenemos", "tenéis", "tienen"],
    preterite: ["tuve", "tuviste", "tuvo", "tuvimos", "tuvisteis", "tuvieron"],
    imperfect: ["tenía", "tenías", "tenía", "teníamos", "teníais", "tenían"],
    future: ["tendré", "tendrás", "tendrá", "tendremos", "tendréis", "tendrán"],
    gerund: "teniendo",
    participle: "tenido",
  },
  hacer: {
    present: ["hago", "haces", "hace", "hacemos", "hacéis", "hacen"],
    preterite: ["hice", "hiciste", "hizo", "hicimos", "hicisteis", "hicieron"],
    imperfect: ["hacía", "hacías", "hacía", "hacíamos", "hacíais", "hacían"],
    future: ["haré", "harás", "hará", "haremos", "haréis", "harán"],
    gerund: "haciendo",
    participle: "hecho",
  },
  poder: {
    present: ["puedo", "puedes", "puede", "podemos", "podéis", "pueden"],
    preterite: ["pude", "pudiste", "pudo", "pudimos", "pudisteis", "pudieron"],
    imperfect: ["podía", "podías", "podía", "podíamos", "podíais", "podían"],
    future: ["podré", "podrás", "podrá", "podremos", "podréis", "podrán"],
    gerund: "pudiendo",
    participle: "podido",
  },
  poner: {
    present: ["pongo", "pones", "pone", "ponemos", "ponéis", "ponen"],
    preterite: ["puse", "pusiste", "puso", "pusimos", "pusisteis", "pusieron"],
    imperfect: ["ponía", "ponías", "ponía", "poníamos", "poníais", "ponían"],
    future: ["pondré", "pondrás", "pondrá", "pondremos", "pondréis", "pondrán"],
    gerund: "poniendo",
    participle: "puesto",
  },
  querer: {
    present: ["quiero", "quieres", "quiere", "queremos", "queréis", "quieren"],
    preterite: ["quise", "quisiste", "quiso", "quisimos", "quisisteis", "quisieron"],
    imperfect: ["quería", "querías", "quería", "queríamos", "queríais", "querían"],
    future: ["querré", "querrás", "querrá", "querremos", "querréis", "querrán"],
    gerund: "queriendo",
    participle: "querido",
  },
  saber: {
    present: ["sé", "sabes", "sabe", "sabemos", "sabéis", "saben"],
    preterite: ["supe", "supiste", "supo", "supimos", "supisteis", "supieron"],
    imperfect: ["sabía", "sabías", "sabía", "sabíamos", "sabíais", "sabían"],
    future: ["sabré", "sabrás", "sabrá", "sabremos", "sabréis", "sabrán"],
    gerund: "sabiendo",
    participle: "sabido",
  },
  decir: {
    present: ["digo", "dices", "dice", "decimos", "decís", "dicen"],
    preterite: ["dije", "dijiste", "dijo", "dijimos", "dijisteis", "dijeron"],
    imperfect: ["decía", "decías", "decía", "decíamos", "decíais", "decían"],
    future: ["diré", "dirás", "dirá", "diremos", "diréis", "dirán"],
    gerund: "diciendo",
    participle: "dicho",
  },
  ver: {
    present: ["veo", "ves", "ve", "vemos", "veis", "ven"],
    preterite: ["vi", "viste", "vio", "vimos", "visteis", "vieron"],
    imperfect: ["veía", "veías", "veía", "veíamos", "veíais", "veían"],
    future: ["veré", "verás", "verá", "veremos", "veréis", "verán"],
    gerund: "viendo",
    participle: "visto",
  },
  dar: {
    present: ["doy", "das", "da", "damos", "dais", "dan"],
    preterite: ["di", "diste", "dio", "dimos", "disteis", "dieron"],
    imperfect: ["daba", "dabas", "daba", "dábamos", "dabais", "daban"],
    future: ["daré", "darás", "dará", "daremos", "daréis", "darán"],
    gerund: "dando",
    participle: "dado",
  },
  venir: {
    present: ["vengo", "vienes", "viene", "venimos", "venís", "vienen"],
    preterite: ["vine", "viniste", "vino", "vinimos", "vinisteis", "vinieron"],
    imperfect: ["venía", "venías", "venía", "veníamos", "veníais", "venían"],
    future: ["vendré", "vendrás", "vendrá", "vendremos", "vendréis", "vendrán"],
    gerund: "viniendo",
    participle: "venido",
  },
};

const PRONOUNS = ["yo", "tú", "él/ella/usted", "nosotros", "vosotros", "ellos/ellas/ustedes"];

const IRREGULAR_PARTICIPLES = {
  abrir: "abierto",
  cubrir: "cubierto",
  decir: "dicho",
  escribir: "escrito",
  hacer: "hecho",
  morir: "muerto",
  poner: "puesto",
  resolver: "resuelto",
  romper: "roto",
  ver: "visto",
  volver: "vuelto",
  devolver: "devuelto",
  descubrir: "descubierto",
  imprimir: "impreso",
  satisfacer: "satisfecho",
  describir: "descrito",
};

const HABER_PRESENT = ["he", "has", "ha", "hemos", "habéis", "han"];

/**
 * Regular conjugation generator
 */
function conjugateRegularVerb(infinitive) {
  const verb = infinitive.trim().toLowerCase();
  const stemAr = verb.endsWith("ar") ? verb.slice(0, -2) : null;
  const stemEr = verb.endsWith("er") ? verb.slice(0, -2) : null;
  const stemIr = verb.endsWith("ir") ? verb.slice(0, -2) : null;

  let participle = IRREGULAR_PARTICIPLES[verb] || null;

  if (stemAr !== null) {
    if (!participle) participle = stemAr + "ado";
    return {
      present: [stemAr + "o", stemAr + "as", stemAr + "a", stemAr + "amos", stemAr + "áis", stemAr + "an"],
      preterite: [stemAr + "é", stemAr + "aste", stemAr + "ó", stemAr + "amos", stemAr + "asteis", stemAr + "aron"],
      imperfect: [stemAr + "aba", stemAr + "abas", stemAr + "aba", stemAr + "ábamos", stemAr + "abais", stemAr + "aban"],
      perfect: HABER_PRESENT.map((h) => `${h} ${participle}`),
      future: [verb + "é", verb + "ás", verb + "á", verb + "emos", verb + "éis", verb + "án"],
      gerund: stemAr + "ando",
      participle: participle,
    };
  }

  if (stemEr !== null) {
    if (!participle) participle = stemEr + "ido";
    return {
      present: [stemEr + "o", stemEr + "es", stemEr + "e", stemEr + "emos", stemEr + "éis", stemEr + "en"],
      preterite: [stemEr + "í", stemEr + "iste", stemEr + "ió", stemEr + "imos", stemEr + "isteis", stemEr + "ieron"],
      imperfect: [stemEr + "ía", stemEr + "ías", stemEr + "ía", stemEr + "íamos", stemEr + "íais", stemEr + "ían"],
      perfect: HABER_PRESENT.map((h) => `${h} ${participle}`),
      future: [verb + "é", verb + "ás", verb + "á", verb + "emos", verb + "éis", verb + "án"],
      gerund: stemEr + "iendo",
      participle: participle,
    };
  }

  if (stemIr !== null) {
    if (!participle) participle = stemIr + "ido";
    return {
      present: [stemIr + "o", stemIr + "es", stemIr + "e", stemIr + "imos", stemIr + "ís", stemIr + "en"],
      preterite: [stemIr + "í", stemIr + "iste", stemIr + "ió", stemIr + "imos", stemIr + "isteis", stemIr + "ieron"],
      imperfect: [stemIr + "ía", stemIr + "ías", stemIr + "ía", stemIr + "íamos", stemIr + "íais", stemIr + "ían"],
      perfect: HABER_PRESENT.map((h) => `${h} ${participle}`),
      future: [verb + "é", verb + "ás", verb + "á", verb + "emos", verb + "éis", verb + "án"],
      gerund: stemIr + "iendo",
      participle: participle,
    };
  }

  return null;
}

/**
 * Extracts clean base lemma and any embedded notes from raw spanish string
 */
export function extractBaseWord(rawSpanish) {
  if (!rawSpanish) return { base: "", notes: "" };
  const str = String(rawSpanish).trim();
  const match = str.match(/^(.*?)\s*\((.*?)\)$/);
  if (match) {
    return {
      base: match[1].trim(),
      notes: match[2].trim(),
    };
  }
  return { base: str, notes: "" };
}

/**
 * Generates noun forms (singular, plural, gender article)
 */
export function getNounForms(baseNoun) {
  let noun = baseNoun.trim().toLowerCase();
  let article = "el";
  let pluralArticle = "los";

  // Check if article was included in raw string
  if (noun.startsWith("el ")) {
    noun = noun.slice(3).trim();
    article = "el";
    pluralArticle = "los";
  } else if (noun.startsWith("la ")) {
    noun = noun.slice(3).trim();
    article = "la";
    pluralArticle = "las";
  } else if (noun.startsWith("un ")) {
    noun = noun.slice(3).trim();
    article = "el (un)";
    pluralArticle = "los (unos)";
  } else if (noun.startsWith("una ")) {
    noun = noun.slice(4).trim();
    article = "la (una)";
    pluralArticle = "las (unas)";
  } else if (noun.endsWith("a") || noun.endsWith("ción") || noun.endsWith("sión") || noun.endsWith("dad")) {
    article = "la";
    pluralArticle = "las";
  }

  // Pluralization rules
  let pluralNoun = noun;
  if (/[aeiouáéó]$/i.test(noun)) {
    pluralNoun = noun + "s";
  } else if (noun.endsWith("z")) {
    pluralNoun = noun.slice(0, -1) + "ces";
  } else if (/[bcdfghjklmnpqrstvwxyz]$/i.test(noun)) {
    pluralNoun = noun + "es";
  }

  return {
    singular: `${article} ${noun}`,
    plural: `${pluralArticle} ${pluralNoun}`,
    gender: article.includes("la") ? "Femenino (阴性)" : "Masculino (阳性)",
  };
}

/**
 * Generates adjective forms
 */
export function getAdjectiveForms(baseAdj) {
  let adj = baseAdj.trim().toLowerCase();
  let mascSing = adj;
  let femSing = adj;
  let mascPlur = adj + "s";
  let femPlur = adj + "s";
  let adverb = adj + "mente";

  if (adj.endsWith("o")) {
    const stem = adj.slice(0, -1);
    mascSing = adj;
    femSing = stem + "a";
    mascPlur = stem + "os";
    femPlur = stem + "as";
    adverb = stem + "amente";
  } else if (adj.endsWith("or") || adj.endsWith("ón") || adj.endsWith("án")) {
    mascSing = adj;
    femSing = adj + "a";
    mascPlur = adj + "es";
    femPlur = adj + "as";
  } else if (/[aeiou]$/i.test(adj)) {
    mascSing = adj;
    femSing = adj;
    mascPlur = adj + "s";
    femPlur = adj + "s";
  } else {
    mascSing = adj;
    femSing = adj;
    mascPlur = adj + "es";
    femPlur = adj + "es";
  }

  return {
    masculineSingular: mascSing,
    feminineSingular: femSing,
    masculinePlural: mascPlur,
    femininePlural: femPlur,
    adverb: adverb,
  };
}

/**
 * Main function: resolves full inflection data for any vocabulary item
 */
export function resolveInflections(item) {
  if (!item) return null;
  const { base, notes } = extractBaseWord(item.spanish);
  const pos = (item.type || "").toLowerCase();
  const customInflections = item.inflections || null;

  // 1. Verb
  if (pos.includes("verb") || base.endsWith("ar") || base.endsWith("er") || base.endsWith("ir")) {
    let conj = null;
    const lowerBase = base.toLowerCase();

    // Check irregular table
    if (COMMON_IRREGULARS[lowerBase]) {
      conj = { ...COMMON_IRREGULARS[lowerBase] };
    } else {
      conj = conjugateRegularVerb(lowerBase);
    }

    if (conj && conj.participle && !conj.perfect) {
      conj.perfect = HABER_PRESENT.map((h) => `${h} ${conj.participle}`);
    }

    if (customInflections && customInflections.perfect && conj) {
      conj.perfect = customInflections.perfect;
    }

    return {
      type: "verb",
      base,
      notes,
      customInflections,
      conjugations: conj,
      pronouns: PRONOUNS,
    };
  }

  // 2. Noun
  if (pos.includes("noun") || pos.includes("sustantivo") || pos.includes("nombre")) {
    const nounForms = getNounForms(base);
    return {
      type: "noun",
      base,
      notes,
      customInflections,
      forms: nounForms,
    };
  }

  // 3. Adjective
  if (pos.includes("adj") || pos.includes("adjetivo")) {
    const adjForms = getAdjectiveForms(base);
    return {
      type: "adjective",
      base,
      notes,
      customInflections,
      forms: adjForms,
    };
  }

  // 4. Phrase / Adverb / Other
  return {
    type: pos || "other",
    base,
    notes,
    customInflections,
  };
}
